import { CheckCircle, Mail } from "lucide-react";
import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { motion } from "motion/react";
import { Button, Input } from "../../ui";
import { RESET_SENT_TEXT, formatResendCountdown, resendSecondsLeft } from "./resetPassword";

interface ResetFormProps {
  resetEmail: string;
  resetEmailSent: boolean;
  resetSubmitting: boolean;
  resetError: string | null;
  /** Момент последней успешной отправки (Date.now()) — от него считается таймер «Отправить ещё раз». */
  resetSentAt: number | null;
  resetResending: boolean;
  onResetEmailChange: (value: string) => void;
  onSubmit: (e: FormEvent) => void;
  onResend: () => void;
  onBackToLogin: () => void;
}

/** Секунды до повторной отправки; тикает раз в секунду, пока не дойдёт до нуля. */
function useResendCountdown(sentAt: number | null): number {
  const [now, setNow] = useState(() => Date.now());
  const left = sentAt === null ? 0 : resendSecondsLeft(sentAt, now);
  useEffect(() => {
    setNow(Date.now());
  }, [sentAt]);
  useEffect(() => {
    if (left <= 0) return;
    const timer = window.setTimeout(() => setNow(Date.now()), 1000);
    return () => window.clearTimeout(timer);
  }, [left, now]);
  return left;
}

export function ResetForm({
  resetEmail,
  resetEmailSent,
  resetSubmitting,
  resetError,
  resetSentAt,
  resetResending,
  onResetEmailChange,
  onSubmit,
  onResend,
  onBackToLogin,
}: ResetFormProps) {
  const secondsLeft = useResendCountdown(resetEmailSent ? resetSentAt : null);
  const canResend = secondsLeft <= 0 && !resetResending;
  return (
    <form onSubmit={onSubmit} noValidate className="space-y-4">
      {resetEmailSent ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center py-8"
        >
          <div className="flex justify-center mb-4">
            <div className="size-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="size-10 text-green-600" />
            </div>
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">{RESET_SENT_TEXT.title}</h3>
          {/* Текст нейтральный (#1342): сервер одинаково отвечает для любого адреса,
              а аккаунтам Telegram/VK без почты письмо не уходит вовсе. */}
          <p className="text-gray-600 mb-3">{RESET_SENT_TEXT.body}</p>
          <p className="text-sm text-gray-500 mb-6">{RESET_SENT_TEXT.socialHint}</p>
          <button
            type="button"
            onClick={onBackToLogin}
            className="rla-btn rla-btn-cta rla-btn-full"
          >
            Вернуться ко входу
          </button>
          <div className="pt-4">
            <Button
              type="button"
              onClick={onResend}
              disabled={!canResend}
              variant="link"
              className="text-sm text-gray-600 hover:text-red-600 -my-2 py-2 px-3 -mx-3"
            >
              {resetResending
                ? "Отправляем..."
                : canResend
                  ? "Отправить ещё раз"
                  : `Отправить ещё раз через ${formatResendCountdown(secondsLeft)}`}
            </Button>
          </div>
        </motion.div>
      ) : (
        <>
          <div className="mb-6 text-center">
            <p className="text-sm text-gray-600">
              Мы отправим вам ссылку для сброса пароля на&nbsp;email
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-gray-400" />
              <Input
                type="email"
                size="lg"
                autoComplete="email"
                value={resetEmail}
                onChange={(e) => onResetEmailChange(e.target.value)}
                placeholder="example@mail.ru"
                error={Boolean(resetError)}
                className="pl-10"
              />
            </div>
            {resetError && <p className="text-red-500 text-xs mt-1">{resetError}</p>}
          </div>
          <button
            type="submit"
            disabled={resetSubmitting}
            className="rla-btn rla-btn-cta rla-btn-full"
          >
            <Mail className="size-5" />
            {resetSubmitting ? "Отправляем..." : "Отправить ссылку"}
          </button>
          <div className="text-center pt-4 pb-6">
            <Button
              type="button"
              onClick={onBackToLogin}
              variant="link"
              className="text-sm text-gray-600 hover:text-red-600 -my-2 py-2 px-3 -mx-3"
            >
              Вернуться ко входу
            </Button>
          </div>
        </>
      )}
    </form>
  );
}
